import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="modal-opener"
// Opens a modal by its ID when clicked
export default class extends Controller {
  static values = { target: String }

  initialize() {
    // Track modals that have been moved to body
    this.modalPortals = new Map()
  }

  open(event) {
    event.preventDefault()
    event.stopPropagation()

    const modalId = this.targetValue
    const modal = document.getElementById(modalId)

    if (modal) {
      // Portal pattern: Move modal to body to escape overflow constraints
      this.moveToBody(modal)

      // Check if modal has a Stimulus controller
      const modalController = this.application.getControllerForElementAndIdentifier(modal, 'modal')

      if (modalController) {
        modalController.open()
      } else {
        // Fallback for modals without controller
        modal.classList.remove('hidden')
        document.body.style.overflow = 'hidden'

        // Focus first input
        const firstInput = modal.querySelector('input:not([type="hidden"]), textarea, select')
        if (firstInput) {
          setTimeout(() => firstInput.focus(), 100)
        }
      }

      // Add close listener to restore position
      this.addCloseListener(modal)
    }
  }

  moveToBody(modal) {
    // If already moved, skip
    if (this.modalPortals.has(modal)) {
      return
    }

    // Store original position
    const originalParent = modal.parentElement
    const originalNextSibling = modal.nextSibling

    // Store the restoration info
    this.modalPortals.set(modal, {
      originalParent,
      originalNextSibling
    })

    // Move to body (this escapes any overflow-hidden containers)
    document.body.appendChild(modal)
  }

  restorePosition(modal) {
    const portalInfo = this.modalPortals.get(modal)

    if (portalInfo) {
      const { originalParent, originalNextSibling } = portalInfo

      // Restore to original position
      if (originalNextSibling) {
        originalParent.insertBefore(modal, originalNextSibling)
      } else {
        originalParent.appendChild(modal)
      }

      // Clean up
      this.modalPortals.delete(modal)
    }
  }

  addCloseListener(modal) {
    // Create observer to detect when modal is hidden
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (modal.classList.contains('hidden')) {
            // Modal was closed, restore its position
            this.restorePosition(modal)
            observer.disconnect()
          }
        }
      })
    })

    // Start observing class changes
    observer.observe(modal, {
      attributes: true,
      attributeFilter: ['class']
    })
  }
}